import { useEffect } from 'react'

export interface PageMeta {
  title?: string | null
  description?: string | null
  image?: string | null
  type?: string
}

type Restore = () => void

// Meta tags live outside React's tree, so they are written straight into <head>
// and reverted on unmount — otherwise an article's meta would leak onto every
// page visited after it.
const setMeta = (attr: 'name' | 'property', key: string, value: string): Restore => {
  const existing = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null

  if (existing) {
    const previous = existing.getAttribute('content')
    existing.setAttribute('content', value)

    return () => {
      if (previous === null) {
        existing.removeAttribute('content')
      } else {
        existing.setAttribute('content', previous)
      }
    }
  }

  const created = document.createElement('meta')
  created.setAttribute(attr, key)
  created.setAttribute('content', value)
  document.head.appendChild(created)

  return () => {
    document.head.removeChild(created)
  }
}

const withBrand = (title: string): string =>
  title.toUpperCase().includes('DOHLAVY') ? title : `${title} · DOHLAVY`

// With null it writes nothing, so a page without meta of its own leaves the
// head to whichever component does own it (an article sets its own).
const usePageMeta = (meta: PageMeta | null): void => {
  const enabled = meta !== null
  const { title, description, image, type = 'website' } = meta || {}

  useEffect(() => {
    if (!enabled) {
      return
    }

    const previousTitle = document.title
    const restores: Restore[] = []

    if (title) {
      document.title = withBrand(title)
      restores.push(() => {
        document.title = previousTitle
      })
      restores.push(setMeta('property', 'og:title', title))
    }

    if (description) {
      restores.push(setMeta('name', 'description', description))
      restores.push(setMeta('property', 'og:description', description))
    }

    if (image) {
      restores.push(setMeta('property', 'og:image', image))
    }

    restores.push(setMeta('property', 'og:type', type))
    restores.push(setMeta('property', 'og:url', window.location.href))

    return () => restores.forEach((restore) => restore())
  }, [enabled, title, description, image, type])
}

export default usePageMeta
